import React, { useState, useEffect } from 'react';
import axios from 'axios';

const PresetManager = ({ onPresetSelect, selectedPresetId, onPresetsLoaded }) => {
  const [presets, setPresets] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newPreset, setNewPreset] = useState({
    name: '',
    units: {
      spear: 0,
      sword: 0,
      axe: 0,
      archer: 0,
      spy: 0,
      light: 0,
      marcher: 0,
      heavy: 0,
      ram: 0,
      catapult: 0,
      knight: 0,
      snob: 0
    },
    isDefault: false
  });
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  useEffect(() => {
    loadPresets();
  }, []);

  const loadPresets = async () => {
    try {
      const response = await axios.get('/api/presets');
      const loadedPresets = response.data.data.presets;
      setPresets(loadedPresets);
      
      // Presets an Parent weiterleiten
      if (onPresetsLoaded) {
        onPresetsLoaded(loadedPresets);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Presets:', error);
    }
  };

  const handleCreatePreset = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post('/api/presets', newPreset);
      setMessage(response.data.message);
      setMessageType('success');
      setShowCreateForm(false);
      setNewPreset({
        name: '',
        units: {
          spear: 0, sword: 0, axe: 0, archer: 0, spy: 0, light: 0,
          marcher: 0, heavy: 0, ram: 0, catapult: 0, knight: 0, snob: 0
        },
        isDefault: false
      });
      loadPresets();
    } catch (error) {
      setMessage(error.response?.data?.message || 'Fehler beim Erstellen des Presets');
      setMessageType('error');
    }
  };

  const handleDeletePreset = async (presetId) => {
    if (!window.confirm('Preset wirklich löschen?')) return;

    try {
      const response = await axios.delete(`/api/presets/${presetId}`);
      setMessage(response.data.message);
      setMessageType('success');
      loadPresets();
    } catch (error) {
      setMessage(error.response?.data?.message || 'Fehler beim Löschen des Presets');
      setMessageType('error');
    }
  };

  const unitLabels = {
    spear: 'Speerträger',
    sword: 'Schwertkämpfer', 
    axe: 'Axtträger',
    archer: 'Bogenschütze',
    spy: 'Späher',
    light: 'Leichte Kavallerie',
    marcher: 'Berittener Bogenschütze',
    heavy: 'Schwere Kavallerie',
    ram: 'Ramme',
    catapult: 'Katapult',
    knight: 'Paladin',
    snob: 'Adelsgeschlecht'
  };

  return (
    <div className="preset-manager">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Vorlagen verwalten</h3>
        <button
          onClick={() => setShowCreateForm(true)}
          className="btn-primary"
        >
          + Neue Vorlage
        </button>
      </div>

      {message && (
        <div className={`alert ${messageType === 'success' ? 'alert-success' : 'alert-error'} mb-4`}>
          {message}
        </div>
      )}

      {/* Preset Liste */}
      <div className="grid gap-4">
        {presets.map((preset) => (
          <div
            key={preset._id}
            className={`border rounded-lg p-4 cursor-pointer transition-colors ${
              selectedPresetId === preset._id
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => onPresetSelect(preset)}
          >
            <div className="flex justify-between items-start">
              <div>
                <h4 className="font-semibold">
                  {preset.name}
                  {preset.isDefault && (
                    <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
                      Standard
                    </span>
                  )}
                </h4>
                <div className="text-sm text-gray-600 mt-2">
                  {Object.entries(preset.units)
                    .filter(([unit, count]) => count > 0)
                    .map(([unit, count]) => `${unitLabels[unit]}: ${count}`)
                    .join(', ')}
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeletePreset(preset._id);
                }}
                className="text-red-600 hover:text-red-800 text-sm"
              >
                Löschen
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Create Preset Dialog */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Neue Vorlage erstellen</h3>
            
            <form onSubmit={handleCreatePreset} className="space-y-4">
              <div>
                <label className="form-label">Name der Vorlage</label>
                <input
                  type="text"
                  value={newPreset.name}
                  onChange={(e) => setNewPreset({...newPreset, name: e.target.value})}
                  className="form-input"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                {Object.entries(unitLabels).map(([unit, label]) => (
                  <div key={unit}>
                    <label className="form-label">{label}</label>
                    <input
                      type="number"
                      min="0"
                      value={newPreset.units[unit]}
                      onChange={(e) => setNewPreset({
                        ...newPreset,
                        units: {
                          ...newPreset.units,
                          [unit]: parseInt(e.target.value) || 0
                        }
                      })}
                      className="form-input"
                    />
                  </div>
                ))}
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isDefault"
                  checked={newPreset.isDefault}
                  onChange={(e) => setNewPreset({...newPreset, isDefault: e.target.checked})}
                  className="mr-2"
                />
                <label htmlFor="isDefault" className="text-sm">Als Standard-Vorlage setzen</label>
              </div>

              <div className="flex space-x-3">
                <button type="submit" className="btn-primary flex-1">
                  Vorlage erstellen
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="btn-secondary flex-1"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PresetManager;
